import { Component, DOCUMENT, inject, input, signal } from '@angular/core'

import { TranslatePipe, TranslateService } from '@ngx-translate/core'

import { EditionsService } from '../../../../../api/editions-service/editions.service'
import { type EditionDto, type MagazineDto } from '../../../../../api/responses'
import { DetailsHeaderComponent } from '../../../../../components/details-header/details-header.component'
import { ErrorToastComponent } from '../../../../../components/error-toast/error-toast.component'
import { SubscribeButtonComponent } from '../../../../../components/subscribe-button/subscribe-button.component'
import { LocalizedDatePipe } from '../../../../../shared/pipes/localized-date.pipe'
import { ContentAccessEnum } from '../../../../../types/types'

@Component({
  selector: 'dhp-edition-header',
  templateUrl: './edition-header.component.html',
  styleUrls: ['./edition-header.component.scss'],
  imports: [
    LocalizedDatePipe,
    TranslatePipe,
    SubscribeButtonComponent,
    DetailsHeaderComponent,
    ErrorToastComponent,
  ],
})
export class EditionHeaderComponent {
  readonly magazine = input.required<MagazineDto>()
  readonly edition = input.required<EditionDto>()
  readonly loading = input<boolean>(false)
  readonly error = input<string | null>(null)
  private readonly editionService = inject(EditionsService)
  private readonly document = inject(DOCUMENT)
  private readonly translateService = inject(TranslateService)

  downloadingEdition = signal<boolean>(false)
  downloadError = signal<string | null>(null)

  get citationManner(): {
    abbreviation?: string
    year?: number
    edition_version: string | null
  } {
    const { abbreviation } = this.magazine()
    const { edition_version, year } = this.edition()
    return { abbreviation, year, edition_version }
  }

  public downloadCopy(): void {
    this.downloadingEdition.set(true)
    this.editionService.downloadEditionPdf(this.edition().id).subscribe({
      next: blob => {
        const url = window.URL.createObjectURL(blob)
        const link = this.document.createElement('a')
        link.href = url
        link.download = `${this.edition().title}.pdf`
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
        this.downloadingEdition.set(false)
      },
      error: () => {
        this.showDownloadError(
          this.translateService.instant(
            'components.journalItem.downloadFailed'
          ) as string
        )
        this.downloadingEdition.set(false)
      },
      complete: () => {
        this.downloadingEdition.set(false)
      },
    })
  }

  /** Shows a download error message and clears it after a delay */
  private showDownloadError(message: string): void {
    this.downloadError.set(message)
    setTimeout(() => {
      this.downloadError.set(null)
    }, 5000)
  }

  protected readonly UserAccessLevel = ContentAccessEnum
}
